import { useCan } from '../../shared/access/AccessControls';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Select,
  SimpleGrid,
  Spacer,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BsBarChartLine } from 'react-icons/bs';
import MainLayout from '../../shared/components/MainLayout';
import Input from '../../shared/components/ui/Input/Input';
import { Paths } from '../../router/routes';
import { useAppSelector } from '../../store/hooks';
import {
  selectCompaniesStats,
  selectCompaniesStatsPeriod,
} from '../../store/slices/companiesSlice';
import { CompanyStats } from '../../store/responseTypes/company';
import { useCompaniesStatsQuery } from './hooks/useCompaniesStatsQuery';
import { useViewerMode } from '../../shared/hooks/useShareToken';
import { useDebounce } from '../../shared/hooks/useDebounce';
import CustomerPickerModal from './components/CustomerPickerModal';
import CompanyHistoryModal from './components/CompanyHistoryModal';
import SavedSelectionsMenu from './components/SavedSelectionsMenu';
import SaveSelectionModal from './components/SaveSelectionModal';
import type { SaveSelectionResult } from './components/SaveSelectionModal';
import ManageSelectionsModal from './components/ManageSelectionsModal';
import type { RenameSelectionResult } from './components/ManageSelectionsModal';
import CompaniesStatsShareModal from './components/CompaniesStatsShareModal';
import {
  SavedSelection,
  loadSavedSelections,
  writeSavedSelections,
  newSelectionId,
} from './savedSelectionsStorage';

const TABULAR = { fontVariantNumeric: 'tabular-nums' as const };

type PresetKey =
  | 'lifetime'
  | 'd1'
  | 'd3'
  | 'd7'
  | 'd14'
  | 'd30'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'last-year'
  | 'custom';

interface PeriodWindow {
  from: string | null; // ISO Z, null = lifetime
  to: string | null;
  label: string; // human-readable, drives all sub-captions
  key: PresetKey; // for the dropdown's `value` binding
}

interface PresetOption {
  key: PresetKey;
  label: string;
}

const PRESET_GROUPS: { label: string; options: PresetOption[] }[] = [
  {
    label: 'Lifetime',
    options: [{ key: 'lifetime', label: 'Since beginning' }],
  },
  {
    label: 'Recent',
    options: [
      { key: 'd1', label: 'Past 24 hr' },
      { key: 'd3', label: 'Past 3 days' },
      { key: 'd7', label: 'Past 7 days' },
      { key: 'd14', label: 'Past 14 days' },
      { key: 'd30', label: 'Past 30 days' },
    ],
  },
  {
    label: 'Calendar',
    options: [
      { key: 'this-quarter', label: 'This quarter' },
      { key: 'last-quarter', label: 'Last quarter' },
      { key: 'this-year', label: 'This year' },
      { key: 'last-year', label: 'Last year' },
    ],
  },
  {
    label: 'Other',
    options: [{ key: 'custom', label: 'Custom range…' }],
  },
];

const PRESET_DAYS: Record<'d1' | 'd3' | 'd7' | 'd14' | 'd30', number> = {
  d1: 1,
  d3: 3,
  d7: 7,
  d14: 14,
  d30: 30,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_SPAN_DAYS = 10 * 366; // BE caps at 10 years; generous on leap years

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const recentPresetLabel = (
  key: 'd1' | 'd3' | 'd7' | 'd14' | 'd30',
): string => {
  if (key === 'd1') return 'in last 24 hours';
  return `in last ${PRESET_DAYS[key]} days`;
};

const quarterLabel = (year: number, quarterIndex: number): string =>
  `in Q${quarterIndex + 1} ${year}`;

const yearLabel = (year: number): string => `in ${year}`;

const customLabelFromIso = (fromIso: string, toIso: string): string => {
  const fromD = new Date(fromIso);
  const toD = new Date(toIso);
  // `to` is half-open exclusive — the inclusive end is one UTC day earlier.
  const incEnd = new Date(
    Date.UTC(toD.getUTCFullYear(), toD.getUTCMonth(), toD.getUTCDate() - 1),
  );
  const sameDay =
    fromD.getUTCFullYear() === incEnd.getUTCFullYear() &&
    fromD.getUTCMonth() === incEnd.getUTCMonth() &&
    fromD.getUTCDate() === incEnd.getUTCDate();
  if (sameDay) {
    return `on ${incEnd.getUTCDate()} ${MONTHS_SHORT[incEnd.getUTCMonth()]} ${incEnd.getUTCFullYear()}`;
  }
  const sameYear = fromD.getUTCFullYear() === incEnd.getUTCFullYear();
  const fromStr = sameYear
    ? `${fromD.getUTCDate()} ${MONTHS_SHORT[fromD.getUTCMonth()]}`
    : `${fromD.getUTCDate()} ${MONTHS_SHORT[fromD.getUTCMonth()]} ${fromD.getUTCFullYear()}`;
  const toStr = `${incEnd.getUTCDate()} ${MONTHS_SHORT[incEnd.getUTCMonth()]} ${incEnd.getUTCFullYear()}`;
  return `from ${fromStr} to ${toStr}`;
};

const buildPresetWindow = (
  key: PresetKey,
  now: Date = new Date(),
): PeriodWindow => {
  switch (key) {
    case 'lifetime':
      return { from: null, to: null, key, label: 'since beginning' };
    case 'd1':
    case 'd3':
    case 'd7':
    case 'd14':
    case 'd30': {
      const days = PRESET_DAYS[key];
      const to = new Date(now.getTime());
      const from = new Date(now.getTime() - days * MS_PER_DAY);
      return {
        from: from.toISOString(),
        to: to.toISOString(),
        key,
        label: recentPresetLabel(key),
      };
    }
    case 'this-quarter': {
      const y = now.getUTCFullYear();
      const q = Math.floor(now.getUTCMonth() / 3);
      const start = Date.UTC(y, q * 3, 1);
      const plannedEnd = Date.UTC(y, (q + 1) * 3, 1);
      const end = Math.min(plannedEnd, now.getTime());
      return {
        from: new Date(start).toISOString(),
        to: new Date(end).toISOString(),
        key,
        label: quarterLabel(y, q),
      };
    }
    case 'last-quarter': {
      const y0 = now.getUTCFullYear();
      const q0 = Math.floor(now.getUTCMonth() / 3);
      const q = q0 === 0 ? 3 : q0 - 1;
      const y = q0 === 0 ? y0 - 1 : y0;
      const start = Date.UTC(y, q * 3, 1);
      const end = Date.UTC(y, (q + 1) * 3, 1);
      return {
        from: new Date(start).toISOString(),
        to: new Date(end).toISOString(),
        key,
        label: quarterLabel(y, q),
      };
    }
    case 'this-year': {
      const y = now.getUTCFullYear();
      const start = Date.UTC(y, 0, 1);
      const plannedEnd = Date.UTC(y + 1, 0, 1);
      const end = Math.min(plannedEnd, now.getTime());
      return {
        from: new Date(start).toISOString(),
        to: new Date(end).toISOString(),
        key,
        label: yearLabel(y),
      };
    }
    case 'last-year': {
      const y = now.getUTCFullYear() - 1;
      const start = Date.UTC(y, 0, 1);
      const end = Date.UTC(y + 1, 0, 1);
      return {
        from: new Date(start).toISOString(),
        to: new Date(end).toISOString(),
        key,
        label: yearLabel(y),
      };
    }
    case 'custom':
      // Caller drives the from/to via the date picker; we never construct
      // a custom window from a key alone.
      return { from: null, to: null, key, label: 'custom range' };
    default: {
      const exhaustive: never = key;
      return { from: null, to: null, key: exhaustive, label: '' };
    }
  }
};

const matchRecentPreset = (
  fromMs: number,
  toMs: number,
): 'd1' | 'd3' | 'd7' | 'd14' | 'd30' | null => {
  const diff = toMs - fromMs;
  const entries: ['d1' | 'd3' | 'd7' | 'd14' | 'd30', number][] = [
    ['d1', PRESET_DAYS.d1],
    ['d3', PRESET_DAYS.d3],
    ['d7', PRESET_DAYS.d7],
    ['d14', PRESET_DAYS.d14],
    ['d30', PRESET_DAYS.d30],
  ];
  // d* presets emit `from = to - N days` from the same `now`, so the
  // millisecond difference is exactly N*MS_PER_DAY. Calendar/custom ranges
  // either use UTC midnights (so the diff is some non-d* multiple of days)
  // or end at "now" (fractional days from midnight). Exact match is safe.
  for (const [key, days] of entries) {
    if (diff === days * MS_PER_DAY) return key;
  }
  return null;
};

interface CalendarMatch {
  key: 'this-quarter' | 'last-quarter' | 'this-year' | 'last-year';
  label: string;
}

const detectCalendarPreset = (
  fromMs: number,
  toMs: number,
  now: Date,
): CalendarMatch | null => {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const q = Math.floor(m / 3);

  const thisQuarterStart = Date.UTC(y, q * 3, 1);
  const nextQuarterStart = Date.UTC(y, (q + 1) * 3, 1);
  const lastQuarterY = q === 0 ? y - 1 : y;
  const lastQuarterQ = q === 0 ? 3 : q - 1;
  const lastQuarterStart = Date.UTC(lastQuarterY, lastQuarterQ * 3, 1);
  const lastQuarterEnd = thisQuarterStart;

  const thisYearStart = Date.UTC(y, 0, 1);
  const nextYearStart = Date.UTC(y + 1, 0, 1);
  const lastYearStart = Date.UTC(y - 1, 0, 1);
  const lastYearEnd = thisYearStart;

  if (fromMs === lastQuarterStart && toMs === lastQuarterEnd) {
    return {
      key: 'last-quarter',
      label: quarterLabel(lastQuarterY, lastQuarterQ),
    };
  }
  if (
    fromMs === thisQuarterStart &&
    toMs > thisQuarterStart &&
    toMs <= nextQuarterStart
  ) {
    return { key: 'this-quarter', label: quarterLabel(y, q) };
  }
  if (fromMs === lastYearStart && toMs === lastYearEnd) {
    return { key: 'last-year', label: yearLabel(y - 1) };
  }
  if (
    fromMs === thisYearStart &&
    toMs > thisYearStart &&
    toMs <= nextYearStart
  ) {
    return { key: 'this-year', label: yearLabel(y) };
  }
  return null;
};

const windowFromIso = (
  from: string,
  to: string,
  now: Date = new Date(),
): PeriodWindow | null => {
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs <= fromMs) return null;
  const recent = matchRecentPreset(fromMs, toMs);
  if (recent !== null) {
    return { from, to, key: recent, label: recentPresetLabel(recent) };
  }
  const calendar = detectCalendarPreset(fromMs, toMs, now);
  if (calendar) {
    return { from, to, key: calendar.key, label: calendar.label };
  }
  return { from, to, key: 'custom', label: customLabelFromIso(from, to) };
};

const LIFETIME_WINDOW: PeriodWindow = {
  from: null,
  to: null,
  key: 'lifetime',
  label: 'since beginning',
};

const parsePeriodFromUrl = (searchParams: URLSearchParams): PeriodWindow => {
  const fromRaw = searchParams.get('from');
  const toRaw = searchParams.get('to');
  if (fromRaw && toRaw) {
    const win = windowFromIso(fromRaw, toRaw);
    if (win) return win;
  }
  return LIFETIME_WINDOW;
};

const periodLabel = (win: PeriodWindow): string => win.label;

const activityPeriodLabel = (win: PeriodWindow): string =>
  win.key === 'lifetime' ? 'in last 30 days' : win.label;

// --- Custom date-picker helpers (UTC midnight wire shape) ---

// Local-calendar Date (Y/M/D in browser TZ) → UTC-midnight ISO of same Y/M/D.
const localDateToUtcMidnightIso = (d: Date): string => {
  const utc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return new Date(utc).toISOString();
};

// Inclusive end Date → exclusive UTC-midnight ISO (next-day midnight UTC).
const localDateToUtcExclusiveEndIso = (d: Date): string => {
  const utc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return new Date(utc).toISOString();
};

// UTC-midnight ISO → local Date representing the same calendar day, so that
// react-datepicker's calendar visually highlights the right cell regardless
// of browser timezone.
const utcMidnightToLocalDate = (iso: string): Date => {
  const d = new Date(iso);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// Exclusive UTC-midnight ISO → local Date for the inclusive end (one day
// before the exclusive midnight).
const utcMidnightExclusiveToInclusiveLocalDate = (iso: string): Date => {
  const d = new Date(iso);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1);
};

const validateCustomRange = (
  from: Date | null,
  to: Date | null,
): string | null => {
  if (!from || !to) return null;
  const fromMs = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toMs = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  if (toMs < fromMs) return 'End date must be on or after start date.';
  // Exclusive end is +1 day; check the closed range vs MAX_SPAN_DAYS.
  const spanDays = (toMs - fromMs) / MS_PER_DAY + 1;
  if (spanDays > MAX_SPAN_DAYS) return 'Range cannot exceed 10 years.';
  return null;
};

const formatNumber = (n: number) => n.toLocaleString('en-US');

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const v = bytes / Math.pow(1000, i);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
};

const todayLabel = () =>
  new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

interface KpiCellProps {
  label: string;
  value: string | number;
  sub: string;
  divider?: boolean;
  lifetimeChip?: boolean;
}

const KpiCell: React.FC<KpiCellProps> = ({
  label,
  value,
  sub,
  divider,
  lifetimeChip,
}) => (
  <Box
    px={{ base: 5, md: 8 }}
    py={{ base: 6, md: 8 }}
    borderLeft={{ md: divider ? '1px solid' : undefined }}
    borderTop={{ base: divider ? '1px solid' : undefined, md: 'none' }}
    borderColor="gray.200"
  >
    <HStack mb={3} align="center" spacing={2}>
      <Text
        fontSize="xs"
        letterSpacing="0.14em"
        textTransform="uppercase"
        color="gray.500"
        fontWeight={500}
      >
        {label}
      </Text>
      {lifetimeChip && (
        <Tag
          size="sm"
          variant="subtle"
          bg="gray.100"
          color="gray.500"
          fontSize="2xs"
          letterSpacing="0.08em"
          textTransform="uppercase"
          fontWeight={500}
          borderRadius="sm"
          px={1.5}
          py={0}
          minH="auto"
        >
          lifetime
        </Tag>
      )}
    </HStack>
    <Heading
      as="div"
      fontSize="6xl"
      fontWeight={500}
      letterSpacing="-0.03em"
      color="gray.900"
      lineHeight={1}
      sx={TABULAR}
    >
      {typeof value === 'number' ? formatNumber(value) : value}
    </Heading>
    <Text fontSize="sm" color="gray.500" mt={3}>
      {sub}
    </Text>
  </Box>
);

interface LeaderboardRowProps {
  rank: number;
  stat: CompanyStats;
  score: number;
  bestScore: number;
  worstScore: number;
  tooltip?: React.ReactNode;
  onSelect: (companyId: string) => void;
}

const LeaderboardRow: React.FC<LeaderboardRowProps> = ({
  rank,
  stat,
  score,
  bestScore,
  worstScore,
  tooltip,
  onSelect,
}) => {
  const range = worstScore - bestScore;
  const pct = range > 0 ? (1 - (score - bestScore) / range) * 100 : 100;
  const row = (
    <Box
      as="button"
      type="button"
      onClick={() => onSelect(stat.companyId)}
      w="100%"
      textAlign="left"
      bg="transparent"
      _focusVisible={{ outline: '2px solid', outlineColor: 'gray.300' }}
    >
      <HStack
        spacing={5}
        py={3}
        px={2}
        mx={-2}
        borderRadius="md"
        _hover={{ bg: 'gray.50' }}
        transition="background 0.15s"
      >
        <Text
          fontSize="xs"
          color="gray.400"
          w={5}
          textAlign="right"
          sx={TABULAR}
        >
          {rank}
        </Text>
        <Text
          fontWeight={500}
          color="gray.900"
          fontSize="sm"
          minW={{ base: '110px', md: '200px' }}
          maxW={{ base: '110px', md: '200px' }}
          noOfLines={1}
        >
          {stat.companyName}
        </Text>
        <Box
          flex={1}
          h="3px"
          bg="gray.100"
          borderRadius="full"
          overflow="hidden"
        >
          <Box
            h="100%"
            bg="gray.900"
            w={`${pct}%`}
            borderRadius="full"
            transition="width 0.5s ease-out"
          />
        </Box>
        <Text
          fontWeight={500}
          color="gray.900"
          fontSize="sm"
          minW="3em"
          textAlign="right"
          sx={TABULAR}
        >
          {formatNumber(score)}
        </Text>
      </HStack>
    </Box>
  );
  if (!tooltip) return row;
  return (
    <Tooltip
      label={tooltip}
      placement="left"
      hasArrow
      bg="gray.900"
      color="white"
      fontSize="xs"
      borderRadius="md"
      px={3}
      py={2}
    >
      <Box>{row}</Box>
    </Tooltip>
  );
};

export type SortKey =
  | 'companyName'
  | 'userSessionsInPeriod'
  | 'signedAgreements'
  | 'registeredAgreements'
  | 'projects'
  | 'storageUsedBytes'
  | 'filesUploaded'
  | 'seats';

export const SORT_KEYS: readonly SortKey[] = [
  'companyName',
  'userSessionsInPeriod',
  'signedAgreements',
  'registeredAgreements',
  'projects',
  'storageUsedBytes',
  'filesUploaded',
  'seats',
] as const;

export const isSortKey = (v: unknown): v is SortKey =>
  typeof v === 'string' && (SORT_KEYS as readonly string[]).includes(v);

const seatSum = (s: CompanyStats) => s.adminUsers + s.employeeUsers;

const BORDA_METRICS = [
  'signedAgreements',
  'registeredAgreements',
  'projects',
  'userSessionsInPeriod',
  'filesUploaded',
] as const;

type BordaMetric = typeof BORDA_METRICS[number];

const BORDA_METRIC_LABEL: Record<BordaMetric, string> = {
  signedAgreements: 'Signed',
  registeredAgreements: 'Registered',
  projects: 'Projects',
  userSessionsInPeriod: 'Sessions',
  filesUploaded: 'Files',
};

interface BordaEntry {
  score: number;
  rank: number;
  perMetricRank: Record<BordaMetric, number>;
}

// Borda rank-sum across the five event-stream metrics. Per metric, customers
// are ranked 1..N descending; ties share the higher rank (1, 2, 2, 4); a zero
// value snaps to rank N+1 so all-zero customers don't drag the middle. The
// composite is the sum (lower = better); tie-break is highest signedAgreements
// then alpha by companyName.
const computeBorda = (stats: CompanyStats[]): Map<string, BordaEntry> => {
  const n = stats.length;
  const ranksByMetric: Record<BordaMetric, Map<string, number>> = {
    signedAgreements: new Map(),
    registeredAgreements: new Map(),
    projects: new Map(),
    userSessionsInPeriod: new Map(),
    filesUploaded: new Map(),
  };

  for (const metric of BORDA_METRICS) {
    const sorted = [...stats].sort(
      (a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0),
    );
    const map = ranksByMetric[metric];
    let i = 0;
    while (i < sorted.length) {
      const v = Number(sorted[i][metric]) || 0;
      if (v === 0) {
        for (let j = i; j < sorted.length; j++) {
          map.set(sorted[j].companyId, n + 1);
        }
        break;
      }
      let j = i;
      while (j < sorted.length && (Number(sorted[j][metric]) || 0) === v) {
        j += 1;
      }
      const rank = i + 1;
      for (let k = i; k < j; k++) {
        map.set(sorted[k].companyId, rank);
      }
      i = j;
    }
  }

  const fallback = n + 1;
  const scored = stats.map((s) => {
    const perMetricRank = {} as Record<BordaMetric, number>;
    let score = 0;
    for (const metric of BORDA_METRICS) {
      const r = ranksByMetric[metric].get(s.companyId) ?? fallback;
      perMetricRank[metric] = r;
      score += r;
    }
    return { stat: s, score, perMetricRank };
  });

  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    if (a.stat.signedAgreements !== b.stat.signedAgreements) {
      return b.stat.signedAgreements - a.stat.signedAgreements;
    }
    return a.stat.companyName.localeCompare(b.stat.companyName);
  });

  const result = new Map<string, BordaEntry>();
  scored.forEach((item, idx) => {
    result.set(item.stat.companyId, {
      score: item.score,
      rank: idx + 1,
      perMetricRank: item.perMetricRank,
    });
  });
  return result;
};

const BORDA_MIN_CUSTOMERS = 5;

// --- Manual customer selection (Task 7) ---

type Selection =
  | { kind: 'default' }
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'ids'; ids: Set<string> };

const DEFAULT_SELECTION_TOP_N = 10;
const SELECTION_ID_CAP = 200;

const parseSelection = (raw: string | null): Selection => {
  if (raw === null) return { kind: 'default' };
  if (raw === 'all') return { kind: 'all' };
  if (raw === 'none') return { kind: 'none' };
  const ids = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (ids.length === 0) return { kind: 'default' };
  return { kind: 'ids', ids: new Set(ids) };
};

const selectionToShowParam = (sel: Selection): string | null => {
  switch (sel.kind) {
    case 'default':
      return null;
    case 'all':
      return 'all';
    case 'none':
      return 'none';
    case 'ids': {
      if (sel.ids.size === 0) return 'none';
      if (sel.ids.size > SELECTION_ID_CAP) {
        // eslint-disable-next-line no-console
        console.warn(
          `[CompaniesStats] Selection of ${sel.ids.size} ids exceeds URL cap (${SELECTION_ID_CAP}); falling back to show=all`,
        );
        return 'all';
      }
      return Array.from(sel.ids).join(',');
    }
    default: {
      const exhaustive: never = sel;
      return exhaustive;
    }
  }
};

const computeDefaultSelectionIds = (
  stats: CompanyStats[],
  bordaIndex: Map<string, BordaEntry>,
  bordaEligible: boolean,
): Set<string> => {
  if (stats.length === 0) return new Set();
  const sorted = bordaEligible
    ? [...stats].sort((a, b) => {
        const ra =
          bordaIndex.get(a.companyId)?.rank ?? Number.MAX_SAFE_INTEGER;
        const rb =
          bordaIndex.get(b.companyId)?.rank ?? Number.MAX_SAFE_INTEGER;
        return ra - rb;
      })
    : [...stats].sort(
        (a, b) => (b.signedAgreements || 0) - (a.signedAgreements || 0),
      );
  return new Set(
    sorted.slice(0, DEFAULT_SELECTION_TOP_N).map((s) => s.companyId),
  );
};

const resolveSelectedIds = (
  stats: CompanyStats[],
  selection: Selection,
  defaultIds: Set<string>,
): Set<string> => {
  switch (selection.kind) {
    case 'all':
      return new Set(stats.map((s) => s.companyId));
    case 'none':
      return new Set();
    case 'default':
      return new Set(defaultIds);
    case 'ids':
      return new Set(selection.ids);
    default: {
      const exhaustive: never = selection;
      return new Set(exhaustive);
    }
  }
};

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => (
  <Tag
    size="sm"
    variant="subtle"
    bg="gray.900"
    color="white"
    borderRadius="full"
    px={2}
    fontWeight={500}
    sx={TABULAR}
  >
    #{rank}
  </Tag>
);

interface SortableThProps {
  label: string;
  sortKey: SortKey;
  isNumeric?: boolean;
  current: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}

const SortableTh: React.FC<SortableThProps> = ({
  label,
  sortKey,
  isNumeric,
  current,
  dir,
  onSort,
}) => {
  const active = current === sortKey;
  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      onClick={() => onSort(sortKey)}
      fontSize="xs"
      letterSpacing="0.1em"
      color={active ? 'gray.900' : 'gray.500'}
      fontWeight={500}
      textTransform="uppercase"
      py={4}
      border="none"
      userSelect="none"
      _hover={{ color: 'gray.700' }}
    >
      {label}
      <Box as="span" ml={1} color="gray.400">
        {active ? (dir === 'desc' ? '↓' : '↑') : ''}
      </Box>
    </Th>
  );
};

const Section: React.FC<{ title: string; aside?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  aside,
  children,
}) => (
  <Box mb={{ base: 12, md: 16 }}>
    <HStack mb={5} align="baseline" flexWrap="wrap" spacing={4}>
      <Text
        fontSize="xs"
        letterSpacing="0.18em"
        textTransform="uppercase"
        color="gray.400"
        fontWeight={500}
      >
        {title}
      </Text>
      <Spacer />
      {aside}
    </HStack>
    {children}
  </Box>
);

const CompaniesStats: React.FC = () => {
  const navigate = useNavigate();
  const viewerMode = useViewerMode();
  const canReadCompany = useCan('ADMIN_COMPANY_READ');
  const canReadBilling = useCan('ADMIN_BILLING_ACCOUNT_READ');
  const canShare = useCan('ADMIN_DASHBOARD_SHARE_LIST');
  const [trigger, { isLoading, isFetching, isError }] =
    useCompaniesStatsQuery();
  const stats = useAppSelector(selectCompaniesStats);
  const displayedSlicePeriod = useAppSelector(
    selectCompaniesStatsPeriod,
    shallowEqual,
  );
  const displayedPeriod: PeriodWindow = useMemo(() => {
    const { fromDate, toDate } = displayedSlicePeriod;
    if (fromDate && toDate) {
      return windowFromIso(fromDate, toDate) ?? LIFETIME_WINDOW;
    }
    return LIFETIME_WINDOW;
  }, [displayedSlicePeriod]);

  const [searchParams] = useSearchParams();
  const period = parsePeriodFromUrl(searchParams);

  // react-router's setSearchParams strips the URL hash (it calls
  // navigate("?" + params)). In viewer mode the hash carries the share token,
  // so we route writes through navigate() to keep `#dst_…` attached.
  const writeSearchParams = useCallback(
    (next: URLSearchParams, opts?: { replace?: boolean }) => {
      navigate(
        { search: `?${next.toString()}`, hash: window.location.hash },
        opts,
      );
    },
    [navigate],
  );

  const handleRowNav = useCallback(
    (companyId: string) => {
      if (!viewerMode && !canReadCompany && !canReadBilling) return;
      const params = new URLSearchParams();
      if (period.from) params.set('from', period.from);
      if (period.to) params.set('to', period.to);
      const search = params.toString() ? `?${params.toString()}` : '';
      const base = viewerMode
        ? `/share/dashboard/${companyId}`
        : `${canReadCompany ? Paths.companies : Paths.billingAccounts}/${companyId}`;
      // Preserve `#dst_…` so the drill-down URL is refresh/bookmark-safe.
      const hash = viewerMode ? window.location.hash : '';
      navigate(`${base}${search}${hash}`);
    },
    [navigate, period.from, period.to, viewerMode, canReadCompany, canReadBilling],
  );

  // Custom-range picker state. Local-only; the URL is the source of truth
  // for *applied* ranges. We mirror the URL into the picker on the first
  // render that lands on a custom URL, and otherwise let the user fill in
  // both halves before pushing back to the URL.
  const [customMode, setCustomMode] = useState<boolean>(period.key === 'custom');
  const [customFrom, setCustomFrom] = useState<Date | null>(() =>
    period.key === 'custom' && period.from
      ? utcMidnightToLocalDate(period.from)
      : null,
  );
  const [customTo, setCustomTo] = useState<Date | null>(() =>
    period.key === 'custom' && period.to
      ? utcMidnightExclusiveToInclusiveLocalDate(period.to)
      : null,
  );

  // Sync the picker if the URL flips into custom externally (back button,
  // shared link). Exiting custom is driven by onPeriodChange, so we don't
  // need to listen for that direction.
  useEffect(() => {
    if (period.key === 'custom') {
      setCustomMode(true);
      if (period.from) setCustomFrom(utcMidnightToLocalDate(period.from));
      if (period.to) setCustomTo(utcMidnightExclusiveToInclusiveLocalDate(period.to));
    }
  }, [period.key, period.from, period.to]);

  // One-shot URL hygiene: migrate legacy `?periodDays=N` to ISO `?from=…&to=…`,
  // and drop orphan / unparseable from/to pairs. Idempotent — re-runs are no-ops.
  useEffect(() => {
    const fromRaw = searchParams.get('from');
    const toRaw = searchParams.get('to');
    const periodDaysRaw = searchParams.get('periodDays');

    let dirty = false;
    const next = new URLSearchParams(searchParams);

    if (periodDaysRaw) {
      const days = Number(periodDaysRaw);
      const presetKey: PresetKey | null =
        days === 1
          ? 'd1'
          : days === 3
          ? 'd3'
          : days === 7
          ? 'd7'
          : days === 14
          ? 'd14'
          : days === 30
          ? 'd30'
          : null;
      if (presetKey && !fromRaw && !toRaw) {
        const presetWin = buildPresetWindow(presetKey);
        if (presetWin.from && presetWin.to) {
          next.set('from', presetWin.from);
          next.set('to', presetWin.to);
        }
      }
      next.delete('periodDays');
      dirty = true;
    }

    const cleanedFrom = next.get('from');
    const cleanedTo = next.get('to');
    if ((cleanedFrom && !cleanedTo) || (!cleanedFrom && cleanedTo)) {
      next.delete('from');
      next.delete('to');
      dirty = true;
    } else if (cleanedFrom && cleanedTo) {
      const fromMs = Date.parse(cleanedFrom);
      const toMs = Date.parse(cleanedTo);
      if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs <= fromMs) {
        next.delete('from');
        next.delete('to');
        dirty = true;
      }
    }

    if (dirty) {
      writeSearchParams(next, { replace: true });
    }
  }, [searchParams, writeSearchParams]);

  const writeFromTo = (
    fromIso: string | null,
    toIso: string | null,
  ) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('periodDays');
    if (fromIso && toIso) {
      nextParams.set('from', fromIso);
      nextParams.set('to', toIso);
    } else {
      nextParams.delete('from');
      nextParams.delete('to');
    }
    writeSearchParams(nextParams, { replace: true });
  };

  const onPeriodChange = (nextKey: PresetKey) => {
    if (nextKey === 'custom') {
      // Reveal the picker; URL changes only once both halves are valid.
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    const nextWindow = buildPresetWindow(nextKey);
    writeFromTo(nextWindow.from, nextWindow.to);
  };

  const customError = useMemo(
    () => validateCustomRange(customFrom, customTo),
    [customFrom, customTo],
  );

  const applyCustomRange = (from: Date | null, to: Date | null) => {
    if (!from || !to) return;
    if (validateCustomRange(from, to) !== null) return;
    const fromIso = localDateToUtcMidnightIso(from);
    const toIso = localDateToUtcExclusiveEndIso(to);
    if (period.from === fromIso && period.to === toIso) return;
    writeFromTo(fromIso, toIso);
  };

  const onCustomFromChange = (d: Date | null) => {
    setCustomFrom(d);
    applyCustomRange(d, customTo);
  };

  const onCustomToChange = (d: Date | null) => {
    setCustomTo(d);
    applyCustomRange(customFrom, d);
  };

  const dropdownValue: PresetKey = customMode ? 'custom' : period.key;
  const customHint =
    customMode && (!customFrom || !customTo)
      ? customFrom
        ? 'Pick an end date to apply.'
        : 'Pick a start date to apply.'
      : null;

  useEffect(() => {
    const promise = trigger({
      limit: 1000,
      offset: 0,
      query: '',
      fromDate: period.from ?? undefined,
      toDate: period.to ?? undefined,
    });
    return () => {
      promise.abort();
    };
  }, [trigger, period.from, period.to]);

  const isRefetching = isFetching && !isLoading;

  const totals = useMemo(
    () =>
      stats.reduce(
        (acc, s) => ({
          companies: acc.companies + 1,
          files: acc.files + s.filesUploaded,
          bytes: acc.bytes + s.storageUsedBytes,
          signed: acc.signed + s.signedAgreements,
          registered: acc.registered + s.registeredAgreements,
          projects: acc.projects + s.projects,
          admins: acc.admins + s.adminUsers,
          employees: acc.employees + s.employeeUsers,
        }),
        {
          companies: 0,
          files: 0,
          bytes: 0,
          signed: 0,
          registered: 0,
          projects: 0,
          admins: 0,
          employees: 0,
        },
      ),
    [stats],
  );

  const activeCompanies = useMemo(
    () => stats.filter((s) => s.userSessionsInPeriod > 0).length,
    [stats],
  );
  const enrolledCompanies = useMemo(
    () =>
      stats.filter((s) => s.signedAgreements + s.registeredAgreements > 0)
        .length,
    [stats],
  );

  const bordaIndex = useMemo(() => computeBorda(stats), [stats]);
  const bordaEligible = stats.length >= BORDA_MIN_CUSTOMERS;
  const topByBorda = useMemo(() => {
    if (!bordaEligible) return [] as CompanyStats[];
    return [...stats]
      .sort((a, b) => {
        const ra = bordaIndex.get(a.companyId)?.rank ?? Number.MAX_SAFE_INTEGER;
        const rb = bordaIndex.get(b.companyId)?.rank ?? Number.MAX_SAFE_INTEGER;
        return ra - rb;
      })
      .slice(0, 8);
  }, [stats, bordaIndex, bordaEligible]);
  // Range of composite scores within the visible top-N — used to normalise
  // the leaderboard bar. Lower score = better rank → longer bar.
  const bordaScoreRange = useMemo(() => {
    if (topByBorda.length === 0) return { best: 0, worst: 0 };
    const scores = topByBorda.map(
      (s) => bordaIndex.get(s.companyId)?.score ?? 0,
    );
    return { best: Math.min(...scores), worst: Math.max(...scores) };
  }, [topByBorda, bordaIndex]);
  const top3Share = useMemo(() => {
    if (totals.signed === 0) return 0;
    const top3 = topByBorda
      .slice(0, 3)
      .reduce((sum, s) => sum + s.signedAgreements, 0);
    return (top3 / totals.signed) * 100;
  }, [topByBorda, totals.signed]);

  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 200);
  const [sortKey, setSortKey] = useState<SortKey>('signedAgreements');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir(key === 'companyName' ? 'asc' : 'desc');
    }
  };

  // --- Selection (Task 7) ---
  const selection = useMemo(
    () => parseSelection(searchParams.get('show')),
    [searchParams],
  );

  const defaultSelectionIds = useMemo(
    () => computeDefaultSelectionIds(stats, bordaIndex, bordaEligible),
    [stats, bordaIndex, bordaEligible],
  );

  const selectedIds = useMemo(
    () => resolveSelectedIds(stats, selection, defaultSelectionIds),
    [stats, selection, defaultSelectionIds],
  );

  const writeSelection = (next: Selection) => {
    const params = new URLSearchParams(searchParams);
    const value = selectionToShowParam(next);
    if (value === null) params.delete('show');
    else params.set('show', value);
    writeSearchParams(params, { replace: true });
  };

  const onRowSelectToggle = (companyId: string) => {
    const next = new Set(selectedIds);
    if (next.has(companyId)) next.delete(companyId);
    else next.add(companyId);
    if (next.size === 0) writeSelection({ kind: 'none' });
    else if (next.size === stats.length) writeSelection({ kind: 'all' });
    else writeSelection({ kind: 'ids', ids: next });
  };

  const onHeaderSelectToggle = () => {
    if (selectedIds.size === stats.length && stats.length > 0) {
      writeSelection({ kind: 'none' });
    } else {
      writeSelection({ kind: 'all' });
    }
  };

  const onSelectAllAction = () => {
    const q = debounced.trim().toLowerCase();
    if (q) {
      const matchingIds = stats
        .filter((s) => s.companyName.toLowerCase().includes(q))
        .map((s) => s.companyId);
      const next = new Set(selectedIds);
      matchingIds.forEach((id) => next.add(id));
      if (next.size === stats.length) writeSelection({ kind: 'all' });
      else writeSelection({ kind: 'ids', ids: next });
    } else {
      writeSelection({ kind: 'all' });
    }
  };

  const onClearSelection = () => writeSelection({ kind: 'none' });
  const onResetSelection = () => writeSelection({ kind: 'default' });

  const toast = useToast();
  const [savedSelections, setSavedSelections] = useState<SavedSelection[]>(
    () => loadSavedSelections(),
  );

  const persistSavedSelections = (next: SavedSelection[]): boolean => {
    const ok = writeSavedSelections(next);
    if (ok) {
      setSavedSelections(next);
    } else {
      toast({
        title: 'Couldn’t save selection.',
        description: 'Storage unavailable (quota or private mode).',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
    return ok;
  };

  const saveModal = useDisclosure();
  const manageModal = useDisclosure();
  const shareModal = useDisclosure();

  const onSaveSubmit = (
    args:
      | { kind: 'create'; name: string }
      | { kind: 'replace'; id: string; name: string },
  ): SaveSelectionResult => {
    if (selectedIds.size > SELECTION_ID_CAP) {
      // Refuse symmetrically with onLoadSaved: storing a selection larger
      // than the URL cap would persist a snapshot that the load handler
      // can't restore once `stats.length` drifts above the saved size.
      toast({
        title: 'Selection too large to save.',
        description: `Selection of ${selectedIds.size} customers exceeds the URL cap (${SELECTION_ID_CAP}). Reduce the selection first.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return 'cap-exceeded';
    }
    const ids = Array.from(selectedIds);
    const nowIso = new Date().toISOString();
    const sortSnapshot = { key: sortKey, direction: sortDir };
    if (args.kind === 'create') {
      const next: SavedSelection = {
        id: newSelectionId(),
        name: args.name.trim(),
        companyIds: ids,
        sort: sortSnapshot,
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      return persistSavedSelections([...savedSelections, next])
        ? 'ok'
        : 'storage-failed';
    }
    const next = savedSelections.map((s) =>
      s.id === args.id
        ? {
            ...s,
            name: args.name.trim(),
            companyIds: ids,
            sort: sortSnapshot,
            updatedAt: nowIso,
          }
        : s,
    );
    return persistSavedSelections(next) ? 'ok' : 'storage-failed';
  };

  const onRenameSaved = (id: string, name: string): RenameSelectionResult => {
    const trimmed = name.trim();
    // Modal guards against empty input, but treat it as a collision-style
    // "didn't happen" for defence in depth.
    if (!trimmed) return 'duplicate';
    const collision = savedSelections.some(
      (s) =>
        s.id !== id && s.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (collision) return 'duplicate';
    const next = savedSelections.map((s) =>
      s.id === id
        ? { ...s, name: trimmed, updatedAt: new Date().toISOString() }
        : s,
    );
    return persistSavedSelections(next) ? 'ok' : 'storage-failed';
  };

  const onDeleteSaved = (id: string): boolean =>
    persistSavedSelections(savedSelections.filter((s) => s.id !== id));

  const onLoadSaved = (saved: SavedSelection) => {
    const present = new Set(stats.map((s) => s.companyId));
    const intersect = saved.companyIds.filter((id) => present.has(id));
    const missing = saved.companyIds.length - intersect.length;
    if (intersect.length === 0) {
      toast({
        title: `“${saved.name}” has no matching customers.`,
        description: 'All saved customers no longer exist in the current view.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    const nextIds = new Set(intersect);
    // Cap-check before the "loaded with missing" notice — otherwise a
    // success toast would fire and then the load would still bail out
    // when the intersection sits above SELECTION_ID_CAP.
    if (nextIds.size !== stats.length && nextIds.size > SELECTION_ID_CAP) {
      // Beyond this cap, selectionToShowParam would silently coerce the URL
      // to `show=all`, which displays every customer instead of the saved
      // subset. Refuse the load and surface the reason.
      toast({
        title: `Can't load “${saved.name}”.`,
        description: `Selection of ${nextIds.size} customers exceeds the URL cap (${SELECTION_ID_CAP}).`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (missing > 0) {
      toast({
        title: `Loaded “${saved.name}”.`,
        description: `${intersect.length} customers loaded; ${missing} no longer exist.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }

    if (nextIds.size === stats.length) {
      writeSelection({ kind: 'all' });
    } else {
      writeSelection({ kind: 'ids', ids: nextIds });
    }

    if (isSortKey(saved.sort.key)) {
      setSortKey(saved.sort.key);
      setSortDir(saved.sort.direction);
    } else {
      toast({
        title: `Loaded “${saved.name}” — sort skipped.`,
        description: 'Saved sort column no longer exists; kept current sort.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const picker = useDisclosure();
  const onPickerApply = (ids: Set<string>) => {
    if (ids.size === 0) writeSelection({ kind: 'none' });
    else if (ids.size === stats.length) writeSelection({ kind: 'all' });
    else writeSelection({ kind: 'ids', ids });
  };

  const [historyFor, setHistoryFor] = useState<CompanyStats | null>(null);
  const onOpenHistory = (s: CompanyStats) => setHistoryFor(s);
  const onCloseHistory = () => setHistoryFor(null);

  // Logical selection count for the toolbar chip — counts only ids that
  // resolve to a real customer in the current `stats` snapshot (stale ids
  // from a shared URL are ignored).
  const selectedCount = useMemo(
    () => stats.filter((s) => selectedIds.has(s.companyId)).length,
    [stats, selectedIds],
  );

  const headerCheckboxIsChecked =
    stats.length > 0 && selectedCount === stats.length;
  const headerCheckboxIsIndeterminate =
    selectedCount > 0 && selectedCount < stats.length;

  const visible = useMemo(() => {
    const eligible = stats.filter((s) => selectedIds.has(s.companyId));
    const q = debounced.trim().toLowerCase();
    const filtered = q
      ? eligible.filter((s) => s.companyName.toLowerCase().includes(q))
      : eligible;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'seats') {
        const an = seatSum(a);
        const bn = seatSum(b);
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = Number(av) || 0;
      const bn = Number(bv) || 0;
      return sortDir === 'asc' ? an - bn : bn - an;
    });
  }, [stats, selectedIds, debounced, sortKey, sortDir]);

  const visibleTotals = useMemo(
    () =>
      visible.reduce(
        (acc, s) => ({
          sessions: acc.sessions + s.userSessionsInPeriod,
          signed: acc.signed + s.signedAgreements,
          registered: acc.registered + s.registeredAgreements,
          projects: acc.projects + s.projects,
          bytes: acc.bytes + s.storageUsedBytes,
          files: acc.files + s.filesUploaded,
          seats: acc.seats + seatSum(s),
        }),
        {
          sessions: 0,
          signed: 0,
          registered: 0,
          projects: 0,
          bytes: 0,
          files: 0,
          seats: 0,
        },
      ),
    [visible],
  );

  const activePct =
    totals.companies > 0 ? (activeCompanies / totals.companies) * 100 : 0;
  const enrolledPct =
    totals.companies > 0 ? (enrolledCompanies / totals.companies) * 100 : 0;

  return (
    <MainLayout>
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 8, md: 12 }}>
        <VStack align="start" spacing={2} mb={{ base: 8, md: 12 }}>
          <Text
            fontSize="xs"
            letterSpacing="0.2em"
            textTransform="uppercase"
            color="gray.400"
            fontWeight={500}
          >
            Companies overview
          </Text>
          <Heading
            as="h1"
            fontSize={{ base: '4xl', md: '6xl' }}
            fontWeight={500}
            letterSpacing="-0.025em"
            color="gray.900"
            lineHeight={1.1}
          >
            Platform health
          </Heading>
          <HStack spacing={3} align="center" flexWrap="wrap">
            <Text fontSize="sm" color="gray.500">
              {totals.companies > 0
                ? `${formatNumber(totals.companies)} customers · `
                : ''}
              As of {todayLabel()}
            </Text>
            <Select
              size="sm"
              variant="unstyled"
              value={dropdownValue}
              onChange={(e) => onPeriodChange(e.target.value as PresetKey)}
              color="gray.700"
              fontSize="sm"
              fontFamily="inherit"
              w="auto"
              cursor="pointer"
              sx={{
                '& > option': { color: 'gray.900' },
                '& > optgroup': { color: 'gray.900' },
              }}
            >
              {PRESET_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
            {isRefetching && (
              <Spinner size="xs" thickness="2px" speed="0.7s" color="gray.400" />
            )}
            {!viewerMode && canShare && (
              <Button
                size="sm"
                variant="outline"
                onClick={shareModal.onOpen}
                ml="auto"
              >
                Share
              </Button>
            )}
          </HStack>
          {customMode && (
            <Box mt={1}>
              <HStack spacing={2} align="center" flexWrap="wrap">
                <Box>
                  <DatePicker
                    selected={customFrom}
                    onChange={onCustomFromChange}
                    selectsStart
                    startDate={customFrom}
                    endDate={customTo}
                    maxDate={customTo ?? new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Start date"
                    isClearable
                    className="companies-stats-datepicker"
                  />
                </Box>
                <Text fontSize="sm" color="gray.500">
                  →
                </Text>
                <Box>
                  <DatePicker
                    selected={customTo}
                    onChange={onCustomToChange}
                    selectsEnd
                    startDate={customFrom}
                    endDate={customTo}
                    minDate={customFrom ?? undefined}
                    maxDate={new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="End date"
                    isClearable
                    className="companies-stats-datepicker"
                  />
                </Box>
              </HStack>
              <Text fontSize="xs" color="gray.400" mt={1}>
                Boundaries use UTC midnight.
              </Text>
              {customError && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {customError}
                </Text>
              )}
              {!customError && customHint && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {customHint}
                </Text>
              )}
            </Box>
          )}
          {isError && stats.length > 0 && (
            <Text fontSize="xs" color="red.500">
              Failed to refresh — numbers below may be stale.
            </Text>
          )}
        </VStack>

        {isLoading && stats.length === 0 ? (
          <HStack justify="center" py={20}>
            <Spinner size="md" thickness="2px" speed="0.7s" color="gray.400" />
          </HStack>
        ) : isError && stats.length === 0 ? (
          <VStack py={20} spacing={2}>
            <Text color="gray.900" fontSize="md" fontWeight={500}>
              Couldn't load stats.
            </Text>
            <Text color="gray.500" fontSize="sm">
              The server returned an error. Try a different period or refresh
              the page.
            </Text>
          </VStack>
        ) : (
          <Box
            opacity={isRefetching ? 0.5 : 1}
            transition="opacity 0.2s"
          >
            <SimpleGrid
              columns={{ base: 1, md: 4 }}
              spacing={0}
              mb={{ base: 12, md: 16 }}
              borderTop="1px solid"
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              <KpiCell
                label="Customers"
                value={totals.companies}
                sub={`${formatNumber(activeCompanies)} active ${activityPeriodLabel(displayedPeriod)}`}
              />
              <KpiCell
                label="Agreements"
                value={totals.signed}
                sub={`${formatNumber(totals.registered)} registered ${periodLabel(displayedPeriod)} · ${formatNumber(totals.projects)} projects ${periodLabel(displayedPeriod)}`}
                divider
              />
              <KpiCell
                label="Storage"
                value={formatBytes(totals.bytes)}
                sub={`${formatNumber(totals.files)} files`}
                divider
                lifetimeChip={displayedPeriod.key !== 'lifetime'}
              />
              <KpiCell
                label="Seats"
                value={totals.admins + totals.employees}
                sub={`${formatNumber(totals.admins)} admins · ${formatNumber(totals.employees)} employees`}
                divider
                lifetimeChip={displayedPeriod.key !== 'lifetime'}
              />
            </SimpleGrid>

            <Section title={`Engagement — ${activityPeriodLabel(displayedPeriod)}`}>
              <HStack mb={3} align="baseline" spacing={3}>
                <Heading
                  as="div"
                  fontSize="4xl"
                  fontWeight={500}
                  color="gray.900"
                  letterSpacing="-0.02em"
                  sx={TABULAR}
                >
                  {formatNumber(activeCompanies)}
                </Heading>
                <Text fontSize="md" color="gray.500">
                  of {formatNumber(totals.companies)} customers active
                </Text>
                <Spacer />
                <Text
                  fontSize="md"
                  color="gray.900"
                  fontWeight={500}
                  sx={TABULAR}
                >
                  {activePct.toFixed(0)}%
                </Text>
              </HStack>
              <Box
                position="relative"
                h="3px"
                bg="gray.100"
                borderRadius="full"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  h="100%"
                  bg="gray.900"
                  w={`${activePct}%`}
                  borderRadius="full"
                  transition="width 0.6s ease-out"
                />
              </Box>
              <Text fontSize="xs" color="gray.400" mt={3}>
                {formatNumber(enrolledCompanies)} customers (
                {enrolledPct.toFixed(0)}%) have at least one signed or
                registered agreement {periodLabel(displayedPeriod)}.
              </Text>
            </Section>

            {topByBorda.length > 0 && (
              <Section
                title="Top customers"
                aside={
                  <Text
                    fontSize="xs"
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                  >
                    by composite ranking — signed, registered, projects,
                    sessions, files
                  </Text>
                }
              >
                <VStack spacing={1} align="stretch">
                  {topByBorda.map((s, i) => {
                    const entry = bordaIndex.get(s.companyId);
                    const tooltip = entry ? (
                      <VStack align="start" spacing={1}>
                        <Text fontWeight={500}>
                          Composite score {entry.score} · rank #{entry.rank}
                        </Text>
                        {BORDA_METRICS.map((m) => (
                          <Text key={m} fontSize="xs" color="gray.200">
                            {BORDA_METRIC_LABEL[m]}: #
                            {entry.perMetricRank[m]}
                          </Text>
                        ))}
                      </VStack>
                    ) : null;
                    return (
                      <LeaderboardRow
                        key={s.companyId}
                        rank={i + 1}
                        stat={s}
                        score={entry?.score ?? 0}
                        bestScore={bordaScoreRange.best}
                        worstScore={bordaScoreRange.worst}
                        tooltip={tooltip}
                        onSelect={handleRowNav}
                      />
                    );
                  })}
                </VStack>
                <Text fontSize="xs" color="gray.400" mt={4}>
                  Top 3 composite leaders contribute {top3Share.toFixed(0)}% of
                  all signed agreements {periodLabel(displayedPeriod)}.
                </Text>
              </Section>
            )}

            <Section
              title="All customers"
              aside={
                <Box w={{ base: '100%', md: '260px' }}>
                  <Input
                    name="search"
                    placeholder="Search…"
                    onChange={setQuery}
                  />
                </Box>
              }
            >
              {!viewerMode && (
                <HStack
                  spacing={2}
                  mb={3}
                  flexWrap="wrap"
                  align="center"
                  fontSize="sm"
                >
                  <Tag
                    size="sm"
                    variant="subtle"
                    bg="gray.100"
                    color="gray.700"
                    borderRadius="full"
                    px={3}
                    fontWeight={500}
                    sx={TABULAR}
                  >
                    Selected: {formatNumber(selectedCount)}
                  </Tag>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={onSelectAllAction}
                    isDisabled={stats.length === 0}
                  >
                    {debounced.trim() ? 'Select all matching' : 'Select all'}
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={onClearSelection}
                    isDisabled={selectedCount === 0}
                  >
                    Clear
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={onResetSelection}
                    isDisabled={selection.kind === 'default'}
                  >
                    Reset
                  </Button>
                  <SavedSelectionsMenu
                    savedSelections={savedSelections}
                    currentIds={selectedIds}
                    selectionKind={selection.kind}
                    canSave={selectedCount > 0 && selection.kind !== 'none'}
                    onLoad={onLoadSaved}
                    onOpenSave={saveModal.onOpen}
                    onOpenManage={manageModal.onOpen}
                  />
                  <Spacer />
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={picker.onOpen}
                    isDisabled={stats.length === 0}
                  >
                    Pick customers…
                  </Button>
                </HStack>
              )}
              <Box
                overflowX="auto"
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <Table size="sm" variant="unstyled">
                  <Thead>
                    <Tr>
                      {!viewerMode && (
                        <Th
                          py={4}
                          border="none"
                          w="36px"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            isChecked={headerCheckboxIsChecked}
                            isIndeterminate={headerCheckboxIsIndeterminate}
                            onChange={onHeaderSelectToggle}
                            aria-label="Toggle all customers"
                          />
                        </Th>
                      )}
                      <SortableTh
                        label="Company"
                        sortKey="companyName"
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Sessions"
                        sortKey="userSessionsInPeriod"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Signed"
                        sortKey="signedAgreements"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Reg"
                        sortKey="registeredAgreements"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Projects"
                        sortKey="projects"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Storage"
                        sortKey="storageUsedBytes"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Files"
                        sortKey="filesUploaded"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <SortableTh
                        label="Seats"
                        sortKey="seats"
                        isNumeric
                        current={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                      />
                      <Th
                        py={4}
                        border="none"
                        w="48px"
                        fontSize="xs"
                        letterSpacing="0.1em"
                        color="gray.500"
                        fontWeight={500}
                        textTransform="uppercase"
                      >
                        {''}
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visible.map((s) => (
                      <Tr
                        key={s.companyId}
                        cursor="pointer"
                        onClick={() => handleRowNav(s.companyId)}
                        borderTop="1px solid"
                        borderColor="gray.100"
                        _hover={{ bg: 'gray.50' }}
                        transition="background 0.15s"
                      >
                        {!viewerMode && (
                          <Td
                            border="none"
                            py={4}
                            w="36px"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              isChecked={selectedIds.has(s.companyId)}
                              onChange={() => onRowSelectToggle(s.companyId)}
                              aria-label={`Toggle ${s.companyName}`}
                            />
                          </Td>
                        )}
                        <Td
                          fontWeight={500}
                          color="gray.900"
                          border="none"
                          py={4}
                          fontSize="sm"
                        >
                          {(() => {
                            if (!bordaEligible) return s.companyName;
                            const entry = bordaIndex.get(s.companyId);
                            if (!entry || entry.rank > 3) return s.companyName;
                            return (
                              <HStack spacing={2}>
                                <RankBadge rank={entry.rank} />
                                <Text as="span">{s.companyName}</Text>
                              </HStack>
                            );
                          })()}
                        </Td>
                        <Td
                          isNumeric
                          color={
                            s.userSessionsInPeriod > 0
                              ? 'gray.900'
                              : 'gray.300'
                          }
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          {s.userSessionsInPeriod || '—'}
                        </Td>
                        <Td
                          isNumeric
                          color={
                            s.signedAgreements > 0 ? 'gray.900' : 'gray.300'
                          }
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          {s.signedAgreements || '—'}
                        </Td>
                        <Td
                          isNumeric
                          color={
                            s.registeredAgreements > 0 ? 'gray.900' : 'gray.300'
                          }
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          {s.registeredAgreements || '—'}
                        </Td>
                        <Td
                          isNumeric
                          color={s.projects > 0 ? 'gray.900' : 'gray.300'}
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          {s.projects || '—'}
                        </Td>
                        <Td
                          isNumeric
                          color={
                            s.storageUsedBytes > 0 ? 'gray.900' : 'gray.300'
                          }
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          {s.storageUsedBytes > 0
                            ? formatBytes(s.storageUsedBytes)
                            : '—'}
                        </Td>
                        <Td
                          isNumeric
                          color={s.filesUploaded > 0 ? 'gray.900' : 'gray.300'}
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          {s.filesUploaded > 0
                            ? formatNumber(s.filesUploaded)
                            : '—'}
                        </Td>
                        <Td
                          isNumeric
                          border="none"
                          py={4}
                          fontSize="sm"
                          sx={TABULAR}
                        >
                          <VStack align="end" spacing={0}>
                            <Text
                              color={seatSum(s) > 0 ? 'gray.900' : 'gray.300'}
                              fontSize="sm"
                              sx={TABULAR}
                            >
                              {formatNumber(seatSum(s))}
                            </Text>
                            <Text fontSize="xs" color="gray.400" sx={TABULAR}>
                              {formatNumber(s.adminUsers)} adm ·{' '}
                              {formatNumber(s.employeeUsers)} emp
                            </Text>
                          </VStack>
                        </Td>
                        <Td
                          border="none"
                          py={4}
                          w="48px"
                          textAlign="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!viewerMode && (
                            <Tooltip
                              label="View history"
                              placement="left"
                              hasArrow
                              openDelay={400}
                            >
                              <Box
                                as="button"
                                type="button"
                                onClick={() => onOpenHistory(s)}
                                aria-label={`View history for ${s.companyName}`}
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                w="28px"
                                h="28px"
                                borderRadius="md"
                                color="gray.500"
                                _hover={{ bg: 'gray.100', color: 'gray.900' }}
                                transition="background 0.15s, color 0.15s"
                              >
                                <Box as={BsBarChartLine} fontSize="14px" />
                              </Box>
                            </Tooltip>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                  {visible.length > 0 && (
                    <Tfoot>
                      <Tr>
                        <Td
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          w="36px"
                          sx={{
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        />
                        <Td
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          Totals · {formatNumber(visible.length)} rows
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatNumber(visibleTotals.sessions)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatNumber(visibleTotals.signed)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatNumber(visibleTotals.registered)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatNumber(visibleTotals.projects)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatBytes(visibleTotals.bytes)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatNumber(visibleTotals.files)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={600}
                          color="gray.900"
                          fontSize="sm"
                          py={4}
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            ...TABULAR,
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        >
                          {formatNumber(visibleTotals.seats)}
                        </Td>
                        <Td
                          py={4}
                          w="48px"
                          borderTop="2px solid"
                          borderColor="gray.200"
                          sx={{
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1,
                          }}
                        />
                      </Tr>
                    </Tfoot>
                  )}
                </Table>
              </Box>
              {visible.length === 0 && stats.length > 0 && (
                <Box py={12} textAlign="center">
                  {selection.kind === 'none' ? (
                    <Text color="gray.400" fontSize="sm">
                      No customers selected. Use Pick customers… or Reset.
                    </Text>
                  ) : selectedCount === 0 ? (
                    <Text color="gray.400" fontSize="sm">
                      No customers in the current selection.
                    </Text>
                  ) : (
                    <Text color="gray.400" fontSize="sm">
                      No selected customers match “{debounced}”.
                    </Text>
                  )}
                </Box>
              )}
            </Section>
            <CustomerPickerModal
              isOpen={picker.isOpen}
              onClose={picker.onClose}
              stats={stats}
              initialSelectedIds={selectedIds}
              onApply={onPickerApply}
            />
            <SaveSelectionModal
              isOpen={saveModal.isOpen}
              onClose={saveModal.onClose}
              currentIds={selectedIds}
              existingSelections={savedSelections}
              onSubmit={(args) => {
                const result = onSaveSubmit(args);
                // Cap-exceeded surfaces a toast and can't be fixed inside the
                // modal — close it so the inline storage-error path doesn't
                // mislabel the failure.
                if (result === 'ok' || result === 'cap-exceeded') {
                  saveModal.onClose();
                }
                return result;
              }}
            />
            <ManageSelectionsModal
              isOpen={manageModal.isOpen}
              onClose={manageModal.onClose}
              savedSelections={savedSelections}
              onRename={onRenameSaved}
              onDelete={onDeleteSaved}
            />
            <CompanyHistoryModal
              company={historyFor}
              isOpen={historyFor !== null}
              onClose={onCloseHistory}
              pagePeriod={{ from: period.from, to: period.to }}
            />
          </Box>
        )}
        {!viewerMode && canShare && (
          <CompaniesStatsShareModal
            isOpen={shareModal.isOpen}
            onClose={shareModal.onClose}
          />
        )}
      </Box>
    </MainLayout>
  );
};

export default CompaniesStats;
